import React, { useState } from 'react';
import { CenterMissingPayment } from 'models';
import { Chip, Colors, PrimaryButton, SecondaryButton, SizeableRoundedDialog, Text, TextArea } from 'library';
import { Card, Grid, styled } from '@mui/material';
import { format12HourTime, formatShortWeekdayMonthDayYear } from 'shared/Dates';
import { client, consoleLogInDev, getTotalHours } from 'shared';
import { WorkerAvatar } from 'parent-portal/providers/cards/shared/WorkerAvatar';

export function MissingPaymentApprovalModal({
    missingPayment,
    onClose,
    open,
}: {
    missingPayment: CenterMissingPayment;
    onClose: () => void;
    open: boolean;
}) {
    const [centerComments, setCenterComments] = useState(missingPayment.center_details || '');
    const [errorMessage, setErrorMessage] = useState('');

    function approveOrReject(action: 'approve' | 'reject') {
        setErrorMessage('');
        client(`api/center-missing-payment/${missingPayment.id}/`, {
            method: 'PATCH',
            body: {
                status: action === 'approve' ? 'CENTER_CONFIRMED' : 'CENTER_REJECTED',
                center_details: centerComments,
            },
        })
            .then(onClose)
            .catch((error) => {
                consoleLogInDev(error);
                setErrorMessage(
                    'There was an error processing this payment. Please try again or reach out to support in the chat or at team@join-tandem.com',
                );
            });
    }

    return (
        <SizeableRoundedDialog open={open} onClose={onClose} contentStyle={styles.dialog} closeButton>
            <Grid container gap={1} flexDirection="column">
                <Text variant="h2" bold textStyle={{ marginBottom: 20 }}>
                    Missing Payment Report
                </Text>
                <HoursCard>
                    <Grid container flexDirection="row" alignItems="center" justifyContent="space-between">
                        <WorkerInfo>
                            <WorkerAvatar src={missingPayment.profile_image?.image} width={50} height={50} />
                            <Text variant="body1" bold textStyle={{ marginTop: 10 }}>
                                {missingPayment.user.first_name} {missingPayment.user.last_name}
                            </Text>
                        </WorkerInfo>
                        <Chip
                            backgroundColor={Colors.mediumRed}
                            textColor={Colors.white}
                            label="Requires Approval"
                            style={{ marginTop: 10 }}
                        />
                    </Grid>
                    <Text variant="body2">
                        <b>Center:</b> {missingPayment.business_location.name}
                    </Text>
                    <Text variant="body2">
                        <b>Date:</b> {formatShortWeekdayMonthDayYear(missingPayment.start_date)}{' '}
                    </Text>
                    <Text variant="body2">
                        <b>Hours Reported:</b> {format12HourTime(missingPayment.start_date, missingPayment.timezone)} -{' '}
                        {format12HourTime(missingPayment.end_date, missingPayment.timezone, true)}
                    </Text>
                    <Text variant="body2">
                        <b>Break:</b> {missingPayment.break_minutes} minutes
                    </Text>
                    <Text variant="body2" bold>
                        Total Hours: {getTotalHours(missingPayment).toFixed(2)}
                    </Text>
                    <Text variant="body2">
                        <b>{missingPayment.user.first_name}&apos;s Comments:</b> {missingPayment.worker_details}
                    </Text>
                </HoursCard>
                <Text variant="body2">
                    <b>Comments:</b> {missingPayment.center_details}
                </Text>
                <TextArea
                    value={centerComments}
                    placeholder="Please provide details about your response. (Required if denying)"
                    onChange={(e) => setCenterComments(e.target.value)}
                    style={{ width: '100%' }}
                />
                <Text variant="body2" align="right" textStyle={{ marginRight: 10 }}>
                    <b>Pay Rate: ${missingPayment.pay_rate}/hour</b>
                </Text>
                <Grid container gap={2} flexDirection="row" justifyContent="flex-end" marginTop={2}>
                    <SecondaryButton
                        disabled={centerComments.length < 15}
                        buttonStyle={{ width: 'auto' }}
                        onClick={() => approveOrReject('reject')}
                    >
                        Deny
                    </SecondaryButton>
                    <PrimaryButton buttonStyle={{ width: 'auto' }} onClick={() => approveOrReject('approve')}>
                        Approve and Pay
                    </PrimaryButton>
                </Grid>
                {errorMessage && (
                    <Text variant="body2" color={Colors.mediumRed} textStyle={styles.errorMessage}>
                        {errorMessage}
                    </Text>
                )}
            </Grid>
        </SizeableRoundedDialog>
    );
}

const styles = {
    dialog: {
        padding: 20,
    },
    errorMessage: {
        marginTop: 10,
    },
};

const HoursCard = styled(Card)({
    backgroundColor: Colors.lightTurq,
    padding: 20,
    paddingTop: 10,
    gap: 10,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 18,
    marginBottom: 20,
});

const WorkerInfo = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
});
